import React, { memo, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedDate, FormattedMessage } from 'react-intl';
import { Form } from 'antd';
import { Form as FinalForm } from 'react-final-form';
import isEqual from 'lodash/isEqual';

import FeedDrawer from 'components/Feed/Drawers/FeedDrawer';
import Link from 'components/Shared/Common/Link';
import UserName from 'components/Shared/User/UserName';

import { getUrl } from 'utils/url';
import FeedInput from 'components/Shared/Forms/FeedInput';
import CategoryTitle from 'components/Shared/Layout/CategoryTitle';
import Icon from 'components/Shared/Common/Icon';
import { StyledActions, StyledLabelInformation } from './styles';

const formatMessages = intl => ({
    drawerTitle: intl.formatMessage({
        id: 'smallDrawer.feed.information.title',
        defaultMessage: 'Paramètres'
    }),
    createdAt: intl.formatMessage({
        id: 'smallDrawer.feed.information.createdAt.title',
        defaultMessage: 'Créé le'
    }),
});

const Information = memo(({
    onClose,
    intl,
    firstname,
    lastname,
    initialValues,
    _user,
    createdAt,
    name,
    validators,
    onSubmit,
    _feed
}) => {
    const path = getUrl('profile', { _user });
    const i18n = useMemo(() => formatMessages(intl), [intl]);
    const _previousFeed = useRef(_feed);

    useEffect(() => {
        if (_previousFeed.current !== _feed) {
            onClose();
            _previousFeed.current = _feed;
        }
    }, [_feed]);

    return (
        <FeedDrawer
            isOpen
            onClose={onClose}
            title={(
                <CategoryTitle
                    label={i18n.drawerTitle}
                    icon={<Icon size="xl" iconName="Settings" />}
                />
            )}
            content={(
                <FinalForm onSubmit={onSubmit} initialValues={initialValues}>
                    {({ handleSubmit, pristine, submitting, valid, form }) => (
                        <Form onSubmit={handleSubmit} layout='vertical'>
                            <FeedInput name="feed" validators={validators} />

                            {createdAt && (
                                <StyledLabelInformation label={i18n.createdAt}>
                                    <FormattedDate
                                        value={createdAt}
                                        year="numeric"
                                        month="long"
                                        day="2-digit"
                                    />
                                    &nbsp;
                                    <FormattedMessage
                                        id="smallDrawer.feed.information.createdAt.name"
                                        defaultMessage={`par ${name}`}
                                        values={{
                                            name: (
                                                <Link href={path} className="name">
                                                    <UserName
                                                        firstname={firstname}
                                                        lastname={lastname}
                                                    />
                                                </Link>
                                            )
                                        }}
                                    />
                                </StyledLabelInformation>
                            )}
                            <StyledActions
                                className="ui-action"
                                pristine={pristine}
                                reset={form.reset}
                                submitting={submitting}
                                submit={form.submit}
                                valid={valid}
                            />
                        </Form>
                    )}
                </FinalForm>
            )}
        />
    );
}, isEqual);

Information.propTypes = {
    intl: PropTypes.any.isRequired,
    createdAt: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    name: PropTypes.string,
    firstname: PropTypes.string,
    lastname: PropTypes.string,
    _user: PropTypes.string,
    _feed: PropTypes.string.isRequired,
    validators: PropTypes.shape({
        required: PropTypes.func.isRequired,
        feedNameLength: PropTypes.func.isRequired,
        feedDescriptionLength: PropTypes.func.isRequired
    }).isRequired
};

export default injectIntl(Information);
